// DichotomyTemplate — design spec §4.
// Two side-by-side panels. Thin vertical divider. One accent-colored.
// Reference: Apple iPhone Pro vs prev comparison + Stripe "Without/With" + Linear method.

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Eyebrow } from '../stage/Eyebrow';
import { PointableItem, DefaultOrbAnchor } from '../stage/PointableItem';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface DichotomyProps {
  content: SceneContent;
}

export function DichotomyTemplate({ content }: DichotomyProps) {
  const { items = [] } = content;
  const left = items[0];
  const right = items[1];
  if (!left || !right) return null;

  return (
    <Stage>
      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.2)}
        className="m-auto w-full max-w-[1440px] grid grid-cols-2 max-md:grid-cols-1 gap-0 relative"
      >
        {/* Vertical divider, desktop. Fades top and bottom. */}
        <motion.span
          aria-hidden
          variants={{
            hidden: { scaleY: 0, opacity: 0 },
            show: { scaleY: 1, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
          }}
          style={{ transformOrigin: 'top' }}
          className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-fg-faint to-transparent"
        />
        {/* Horizontal divider, mobile. */}
        <span
          aria-hidden
          className="md:hidden h-px w-full bg-gradient-to-r from-transparent via-fg-faint to-transparent my-8"
          style={{ gridRow: 2 }}
        />

        {/* LEFT — neutral / "before" */}
        <motion.section variants={fadeUp} className="pr-16 max-md:pr-0">
          <PointableItem orbTargetId="side_0" orbSize={48} orbPlacement="top-right" className="flex flex-col gap-6">
            {left.eyebrow && <Eyebrow>{left.eyebrow}</Eyebrow>}
            <h3 className="text-display-2 font-display text-fg-soft text-balance">{left.headline}</h3>
            {left.body && <p className="text-body text-fg-mute max-w-[42ch]">{left.body}</p>}
            {left.points && left.points.length > 0 && (
              <ul className="flex flex-col gap-3 mt-6">
                {left.points.map((p, i) => (
                  <li key={i} className="text-body text-fg-mute flex gap-3">
                    <span className="text-fg-faint mt-2">—</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}
          </PointableItem>
        </motion.section>

        {/* RIGHT — accent / "after" */}
        <motion.section
          variants={{
            hidden: { opacity: 0, y: 24, x: 16, filter: 'blur(8px)' },
            show: {
              opacity: 1, y: 0, x: 0, filter: 'blur(0px)',
              transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
            },
          }}
          className="pl-16 max-md:pl-0"
        >
          <PointableItem orbTargetId="side_1" orbSize={48} orbPlacement="top-right" className="flex flex-col gap-6">
            {right.eyebrow && (
              <span className="text-eyebrow text-accent uppercase font-medium">{right.eyebrow}</span>
            )}
            <h3 className="text-display-2 font-display text-fg text-balance">{right.headline}</h3>
            {right.body && <p className="text-body text-fg-soft max-w-[42ch]">{right.body}</p>}
            {right.points && right.points.length > 0 && (
              <ul className="flex flex-col gap-3 mt-6">
                {right.points.map((p, i) => (
                  <li key={i} className="text-body text-fg-soft flex gap-3">
                    <span className="text-accent mt-2 font-medium">+</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}
          </PointableItem>
        </motion.section>
      </motion.div>

      {/* Default orb anchor — bottom-center verdict spot per §4g. Hidden
          when the agent is pointing at one of the two sides. */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 max-md:bottom-6">
        <DefaultOrbAnchor size={56} />
      </div>
    </Stage>
  );
}
